import { tutorialTaskDone } from '../../tutorial/events'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useEnv } from '../context'
import { parseSessionKey } from './schedule'
import type { NoteOwner } from './notes'
import { isOnline, useOnline } from '../platform/network'
import type { Platform } from '../platform/platform'
import {
  SliceApiError,
  type AiAction,
  type AiActionDecision,
  type AthleteEventItem,
  type AttendanceStatus,
  type EventParticipant,
  type HubBotChannel,
  type HubContextLink,
  type HubContextRef,
  type Material,
  type OccurrenceChange,
  type OccurrenceMoveResult,
  type OccurrencePreview,
  type OccurrenceResult,
  type ParticipantPatch,
  type SchedulePreview,
  type ScheduleSaveRequest,
  type ScheduleSaveResult,
  type SessionBoard,
} from './types'

const ROOT = 'redesign'

/**
 * SC12: после возврата в мини-приложение (из бота, из свёрнутого
 * мессенджера) открытые экраны перечитывают данные фоном. Именно invalidate, а
 * не reset: прежние данные остаются на экране, пока идёт запрос, - ни пустых
 * состояний, ни скелетонов. Закрытые экраны перечитают своё при открытии.
 */
export function useRefreshOnResume(platform: Platform) {
  const client = useQueryClient()
  useEffect(
    () =>
      platform.onResume(() => {
        void client.invalidateQueries({ queryKey: [ROOT] })
      }),
    [platform, client],
  )
}

export function useClubContext() {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'context'], queryFn: () => api.getContext(), staleTime: 5 * 60_000 })
}

export function useSessions(from: string, to: string) {
  const { api } = useEnv()
  return useQuery({
    queryKey: [ROOT, 'sessions', from, to],
    queryFn: () => api.getSessions(from, to),
    staleTime: 60_000,
  })
}

export function useSessionBoard(key: string) {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'board', key], queryFn: () => api.getBoard(key) })
}

/**
 * Сколько подгруженная заранее доска считается свежей: повторный показ
 * «Сегодня» в этот срок её не перечитывает. Экран занятия с сетью всё равно
 * читает доску заново - заранее подгруженная нужна только без сети.
 */
export const BOARD_PREFETCH_STALE_MS = 10 * 60_000

/**
 * Доски занятий заранее (этап 4, шаг 3): пока есть сеть, подгрузить доски
 * перечисленных занятий с тем же ключом, что у экрана занятия, - снимок на
 * устройстве их сохранит, и в зале без сети откроется любое из них.
 *
 * Только где кэш сохраняется на устройстве (PWA): в мини-приложениях это
 * лишние запросы. Без сети не пытается; по одной доске за раз, сеть пропала -
 * остановка. Свежие (BOARD_PREFETCH_STALE_MS) не перечитываются, поэтому
 * повторный показ экрана запросов не шлёт. Уход с экрана подгрузку не
 * прерывает: тренер обычно сразу открывает ближайшее занятие, а остальные
 * доски нужны тоже. Доска читается без создания занятия (getBoard).
 *
 * ready: список занятий получен с сервера, а не только поднят из снимка.
 */
export function usePrefetchBoards(keys: string[], ready: boolean) {
  const { api, savesOffline } = useEnv()
  const client = useQueryClient()
  const online = useOnline()
  const joined = keys.join('\n')

  useEffect(() => {
    if (!savesOffline || !ready || !online || !joined) return
    const list = joined.split('\n')
    void (async () => {
      for (const key of list) {
        if (!isOnline()) return
        await client.prefetchQuery({
          queryKey: [ROOT, 'board', key],
          queryFn: () => api.getBoard(key),
          staleTime: BOARD_PREFETCH_STALE_MS,
          retry: false,
        })
      }
    })()
  }, [savesOffline, ready, online, joined, api, client])
}

/** Состояние сохранения одной отметки: чтобы в строке было видно, что ушло на сервер. */
export type MarkState =
  | { phase: 'saving'; status: AttendanceStatus | null }
  | { phase: 'error'; status: AttendanceStatus | null }
  | { phase: 'saved' }

/**
 * Отметка уходит на сервер сразу по тапу (решение владельца 2026-10-01). Пока
 * ответа нет, строка показывает «Сохраняю», при сбое - «Не сохранено» и
 * повтор. Доска в кэше обновляется только ответом сервера: успех заранее не
 * обещаем (ТЗ SC12).
 */
export function useMarkAttendance(key: string) {
  const { api, platform } = useEnv()
  const client = useQueryClient()
  const [states, setStates] = useState<Record<string, MarkState>>({})

  const mark = useCallback(
    async (athleteId: string, status: AttendanceStatus | null) => {
      setStates((s) => ({ ...s, [athleteId]: { phase: 'saving', status } }))
      platform.haptic('select')
      try {
        const board = await api.mark(key, athleteId, status)
        // Перечитывание доски, начатое до записи (возврат в приложение,
        // правка спортсмена), вернулось бы позже и стёрло эту отметку.
        await client.cancelQueries({ queryKey: [ROOT, 'board', key] })
        client.setQueryData<SessionBoard>([ROOT, 'board', key], board)
        setStates((s) => ({ ...s, [athleteId]: { phase: 'saved' } }))
      } catch {
        setStates((s) => ({ ...s, [athleteId]: { phase: 'error', status } }))
        platform.haptic('error')
      }
    },
    [api, client, key, platform],
  )

  const retryFailed = useCallback(() => {
    for (const [athleteId, st] of Object.entries(states)) {
      if (st.phase === 'error') void mark(athleteId, st.status)
    }
  }, [mark, states])

  const failed = Object.values(states).filter((s) => s.phase === 'error').length
  const saving = Object.values(states).filter((s) => s.phase === 'saving').length
  return { mark, retryFailed, states, failed, saving }
}

// ---------- перекличка текстом и голосом, новичок на занятии (этап 5, шаг 2) ----------

/**
 * После записи переклички или новичка: доска занятия, журнал группы, списки
 * занятий и спортсменов перечитываются.
 */
export function useInvalidateSessionMarks(groupId: string | undefined) {
  const client = useQueryClient()
  return useCallback(async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: [ROOT, 'board'] }),
      client.invalidateQueries({ queryKey: [ROOT, 'sessions'] }),
      client.invalidateQueries({ queryKey: [ROOT, 'athletes'] }),
      groupId ? client.invalidateQueries({ queryKey: [ROOT, 'journal', groupId] }) : Promise.resolve(),
      groupId ? client.invalidateQueries({ queryKey: [ROOT, 'group-athletes', groupId] }) : Promise.resolve(),
    ])
  }, [client, groupId])
}

// ---------- спортсмены (этап 2) ----------

export function useGroups() {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'groups'], queryFn: () => api.listGroups(), staleTime: 5 * 60_000 })
}

export function useAthletes() {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'athletes'], queryFn: () => api.listAthletes(), staleTime: 60_000 })
}

export function useAthlete(id: string) {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'athlete', id], queryFn: () => api.getAthlete(id) })
}

export function useSportFields(sportName: string | undefined) {
  const { api } = useEnv()
  return useQuery({
    queryKey: [ROOT, 'sport-fields', sportName],
    queryFn: () => api.getSportFields(sportName ?? ''),
    enabled: sportName !== undefined,
    staleTime: 30 * 60_000,
  })
}

export function useAthleteAttendance(id: string, from: string, to: string, enabled: boolean) {
  const { api } = useEnv()
  return useQuery({
    queryKey: [ROOT, 'athlete-attendance', id, from, to],
    queryFn: () => api.getAthleteAttendance(id, from, to),
    enabled,
  })
}

// ---------- записи и материалы (этап 5 шаг 3) ----------

/** Ключ списка записей: у спортсмена и группы - свои разделы снимка без сети. */
export function notesKey(owner: NoteOwner) {
  return [ROOT, owner.kind === 'athlete' ? 'athlete-notes' : 'group-notes', owner.id] as const
}

/** Записи спортсмена или группы, новые первыми. */
export function useNotes(owner: NoteOwner, enabled = true) {
  const { api } = useEnv()
  return useQuery({
    queryKey: notesKey(owner),
    queryFn: () => (owner.kind === 'athlete' ? api.listAthleteNotes(owner.id) : api.listGroupNotes(owner.id)),
    enabled,
  })
}

/**
 * Корзина записей спортсмена или группы. В снимок без сети не входит (раздел
 * не в PERSISTED): без сети корзина не открывается.
 */
export function notesTrashKey(owner: NoteOwner) {
  return [ROOT, owner.kind === 'athlete' ? 'athlete-notes-trash' : 'group-notes-trash', owner.id] as const
}

export function useNotesTrash(owner: NoteOwner, enabled = true) {
  const { api } = useEnv()
  return useQuery({
    queryKey: notesTrashKey(owner),
    queryFn: () => (owner.kind === 'athlete' ? api.listAthleteNotesTrash(owner.id) : api.listGroupNotesTrash(owner.id)),
    enabled,
  })
}

/** Корзина материалов текущего направления (не в снимке без сети). */
export const MATERIALS_TRASH_KEY = [ROOT, 'materials-trash'] as const

export function useMaterialsTrash(enabled = true) {
  const { api } = useEnv()
  return useQuery({ queryKey: MATERIALS_TRASH_KEY, queryFn: () => api.listMaterialsTrash(), enabled })
}

export function useMaterials() {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'materials'], queryFn: () => api.listMaterials() })
}

/** Материал; пока он грузится, на экране - строка из списка (без мерцания). */
export function useMaterial(id: string) {
  const { api } = useEnv()
  const client = useQueryClient()
  return useQuery({
    queryKey: [ROOT, 'material', id],
    queryFn: () => api.getMaterial(id),
    enabled: !!id,
    placeholderData: () => client.getQueryData<Material[]>([ROOT, 'materials'])?.find((m) => m.id === id),
  })
}

/** После изменения спортсмена: карточка, список и составы групп в занятиях. */
export function useInvalidateAthlete() {
  const client = useQueryClient()
  return useCallback(
    (id?: string) => {
      void client.invalidateQueries({ queryKey: [ROOT, 'athletes'] })
      // Состав группы и оплаты зависят от группы и паузы спортсмена
      void client.invalidateQueries({ queryKey: [ROOT, 'group-athletes'] })
      void client.invalidateQueries({ queryKey: [ROOT, 'payments'] })
      void client.invalidateQueries({ queryKey: [ROOT, 'board'] })
      void client.invalidateQueries({ queryKey: [ROOT, 'sessions'] })
      if (id) void client.invalidateQueries({ queryKey: [ROOT, 'athlete', id] })
    },
    [client],
  )
}

// ---------- группы (этап 2) ----------

export function useGroup(id: string) {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'group', id], queryFn: () => api.getGroup(id) })
}

export function useGroupAthletes(id: string) {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'group-athletes', id], queryFn: () => api.listGroupAthletes(id) })
}

export function useGroupJournal(id: string, from: string, to: string, enabled: boolean) {
  const { api } = useEnv()
  return useQuery({
    queryKey: [ROOT, 'journal', id, from, to],
    queryFn: () => api.getGroupJournal(id, from, to),
    enabled,
  })
}

export function useGroupPayments(id: string, yearMonth: string, enabled: boolean) {
  const { api } = useEnv()
  return useQuery({
    queryKey: [ROOT, 'payments', id, yearMonth],
    queryFn: () => api.getGroupPayments(id, yearMonth),
    enabled,
  })
}

/**
 * Правка расписания группы (S06, этап 3 шаг 8): предпросмотр последствий и
 * сохранение. Сохранение не уходит вторым запросом, пока первый в полёте; повтор
 * после сбоя безопасен - сервер не применяет уже сохранённое расписание второй
 * раз. После сохранения перечитываются группа, занятия (Сегодня, расписание),
 * доски занятий и журнал группы.
 */
export function useGroupScheduleSave(groupId: string) {
  const { api, platform } = useEnv()
  const client = useQueryClient()
  const inFlight = useRef(false)

  const preview = useCallback(
    (schedule: ScheduleSaveRequest['schedule'], from: string): Promise<SchedulePreview> =>
      api.previewGroupSchedule(groupId, schedule, from),
    [api, groupId],
  )

  const save = useCallback(
    async (req: ScheduleSaveRequest): Promise<ScheduleSaveResult | null> => {
      if (inFlight.current) return null
      inFlight.current = true
      platform.haptic('select')
      try {
        const res = await api.saveGroupSchedule(groupId, req)
        if (req.schedule.length) tutorialTaskDone('schedule')
        platform.haptic('success')
        await Promise.all([
          client.invalidateQueries({ queryKey: [ROOT, 'group', groupId] }),
          client.invalidateQueries({ queryKey: [ROOT, 'schedule-timeline', groupId] }),
          client.invalidateQueries({ queryKey: [ROOT, 'sessions'] }),
          client.invalidateQueries({ queryKey: [ROOT, 'board'] }),
          client.invalidateQueries({ queryKey: [ROOT, 'journal', groupId] }),
        ])
        return res
      } catch (err) {
        platform.haptic('error')
        throw err
      } finally {
        inFlight.current = false
      }
    },
    [api, client, groupId, platform],
  )

  return { preview, save }
}

/** Текущее и запланированные расписания группы (этап 3 шаг 10). */
export function useGroupScheduleTimeline(groupId: string, today: string) {
  const { api } = useEnv()
  return useQuery({
    queryKey: [ROOT, 'schedule-timeline', groupId, today],
    queryFn: () => api.getGroupScheduleTimeline(groupId, today),
  })
}

/**
 * Одно занятие (этап 3 шаг 10, SC13): предпросмотр, отмена, возврат и
 * перенос. Пока запись в полёте, второе нажатие ничего не шлёт (null). После
 * записи доска занятия обновляется ответом сервера, а занятия («Сегодня»,
 * расписание), доски, группа и её журнал перечитываются.
 */
export function useOccurrence(key: string) {
  const { api, platform } = useEnv()
  const client = useQueryClient()
  const inFlight = useRef(false)
  const groupId = parseSessionKey(key)?.groupId ?? ''

  const preview = useCallback(
    (change: OccurrenceChange): Promise<OccurrencePreview> => api.previewOccurrence(key, change),
    [api, key],
  )

  const refresh = useCallback(
    () =>
      Promise.all([
        client.invalidateQueries({ queryKey: [ROOT, 'sessions'] }),
        client.invalidateQueries({ queryKey: [ROOT, 'board'] }),
        client.invalidateQueries({ queryKey: [ROOT, 'group', groupId] }),
        client.invalidateQueries({ queryKey: [ROOT, 'journal', groupId] }),
      ]),
    [client, groupId],
  )

  const write = useCallback(
    async <T>(call: () => Promise<T>, onDone?: (res: T) => void): Promise<T | null> => {
      if (inFlight.current) return null
      inFlight.current = true
      platform.haptic('select')
      try {
        const res = await call()
        platform.haptic('success')
        onDone?.(res)
        await refresh()
        return res
      } catch (err) {
        platform.haptic('error')
        throw err
      } finally {
        inFlight.current = false
      }
    },
    [platform, refresh],
  )

  const setBoard = useCallback(
    (res: OccurrenceResult) => {
      void client.cancelQueries({ queryKey: [ROOT, 'board', key] })
      client.setQueryData<SessionBoard>([ROOT, 'board', key], res.board)
    },
    [client, key],
  )

  const cancel = useCallback(() => write(() => api.cancelOccurrence(key), setBoard), [api, key, setBoard, write])
  const restore = useCallback(() => write(() => api.restoreOccurrence(key), setBoard), [api, key, setBoard, write])
  const move = useCallback(
    (to: { date: string; startTime: string; endTime?: string }): Promise<OccurrenceMoveResult | null> =>
      write(() => api.moveOccurrence(key, to)),
    [api, key, write],
  )

  // Один объект на время жизни экрана: шторки держат его в зависимостях эффектов.
  return useMemo(() => ({ preview, cancel, restore, move }), [preview, cancel, restore, move])
}

export type PaymentRowState = { phase: 'saving'; paid: boolean } | { phase: 'error'; paid: boolean }

/**
 * Отметка оплаты по строке (S10, SC06): сохраняется сразу, в строке видно
 * «Сохраняю» и сбой с повтором. Повтор безопасен: на сервере upsert по
 * спортсмену, группе и месяцу, второй записи не будет.
 */
export function usePaymentMarks(groupId: string, yearMonth: string) {
  const { api, platform } = useEnv()
  const client = useQueryClient()
  const [states, setStates] = useState<Record<string, PaymentRowState>>({})
  // Другой месяц - другие строки: состояния сохранения прежнего не переносим.
  useEffect(() => setStates({}), [groupId, yearMonth])

  const set = useCallback(
    async (athleteId: string, paid: boolean) => {
      setStates((s) => ({ ...s, [athleteId]: { phase: 'saving', paid } }))
      platform.haptic('select')
      try {
        await api.setPayment(groupId, athleteId, yearMonth, paid)
        await client.invalidateQueries({ queryKey: [ROOT, 'payments', groupId, yearMonth] })
        setStates((s) => {
          const next = { ...s }
          delete next[athleteId]
          return next
        })
      } catch {
        setStates((s) => ({ ...s, [athleteId]: { phase: 'error', paid } }))
        platform.haptic('error')
      }
    },
    [api, client, groupId, platform, yearMonth],
  )

  return { set, states, failed: Object.values(states).filter((s) => s.phase === 'error').length }
}

// ---------- клуб и аккаунт (этап 2) ----------

export function useClubOverview() {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'club-overview'], queryFn: () => api.getClubOverview() })
}

export function useChannels() {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'channels'], queryFn: () => api.getChannels() })
}

export function useTeam() {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'team'], queryFn: () => api.getTeam() })
}

export function useSubscription() {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'subscription'], queryFn: () => api.getSubscription() })
}

/**
 * Расчёт смены тарифа (S19). Перечитывается при каждом открытии: срок и
 * остаток дней меняются со временем. Ключ под 'subscription': после оплаты
 * или смены тарифа всё перечитывается одним invalidate.
 */
export function useTierPreview(tier: string) {
  const { api } = useEnv()
  return useQuery({
    queryKey: [ROOT, 'subscription', 'preview', tier],
    queryFn: () => api.previewTierChange(tier),
    staleTime: 0,
  })
}

/** Свои платежи за тариф и подключена ли оплата картой. */
export function usePaymentHistory() {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'subscription', 'payments'], queryFn: () => api.listPayments() })
}

// ---------- массовые действия Штаба (этап 3, S14, SC08) ----------

/** Снимок действия. Перечитывается при каждом открытии: его могли решить в боте. */
export function useAiAction(id: string) {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'ai-action', id], queryFn: () => api.getAiAction(id), staleTime: 0 })
}

/**
 * Подтверждение и отмена. Пока запрос в полёте, второе нажатие ничего не
 * шлёт; повтор после сбоя безопасен - сервер исполняет действие один раз и
 * на повтор отдаёт сохранённый итог. Снимок в кэше обновляется только ответом
 * сервера: успех заранее не обещаем.
 */
export function useAiActionDecision(id: string) {
  const { api, platform } = useEnv()
  const client = useQueryClient()
  const inFlight = useRef(false)
  const [state, setState] = useState<{
    phase: 'idle' | 'confirming' | 'cancelling' | 'failed'
    decision?: AiActionDecision
    failedKind?: 'confirm' | 'cancel'
  }>({ phase: 'idle' })

  const decide = useCallback(
    async (kind: 'confirm' | 'cancel') => {
      if (inFlight.current) return
      inFlight.current = true
      setState((s) => ({ ...s, phase: kind === 'confirm' ? 'confirming' : 'cancelling' }))
      platform.haptic('select')
      try {
        const decision = kind === 'confirm' ? await api.confirmAiAction(id) : await api.cancelAiAction(id)
        client.setQueryData<AiAction>([ROOT, 'ai-action', id], decision.action)
        setState({ phase: 'idle', decision })
        if (decision.action.status === 'executed') {
          platform.haptic('success')
          // Данные поменялись: экраны оплат, занятий и спортсменов перечитают своё
          void client.invalidateQueries({
            predicate: (q) => q.queryKey[0] === ROOT && q.queryKey[1] !== 'ai-action' && q.queryKey[1] !== 'hub-history',
          })
        }
      } catch {
        setState((s) => ({ ...s, phase: 'failed', failedKind: kind }))
        platform.haptic('error')
      } finally {
        inFlight.current = false
      }
    },
    [api, client, id, platform],
  )

  return { ...state, decide }
}

/** За сколько до срока код считаем устаревшим: пока бот откроется, он истечёт. */
export const HUB_LINK_MARGIN_MS = 30_000

export type HubContextLinkState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; link: HubContextLink }
  | { status: 'error'; error: SliceApiError }

/**
 * Код перехода в бота с контекстом (S16). Запрашивается при открытии экрана
 * «Обсудить в боте», чтобы нажатие «Перейти в бота» открыло ссылку сразу:
 * MAX открывает ссылку только из обработчика нажатия, ждать сервер там
 * нельзя. Код одноразовый и не кэшируется: на каждый показ экрана - свой.
 */
export function useHubContextLink(channel: HubBotChannel | null, ref: HubContextRef | undefined) {
  const { api, now } = useEnv()
  const [state, setState] = useState<HubContextLinkState>(() =>
    channel && ref ? { status: 'loading' } : { status: 'idle' },
  )
  const seq = useRef(0)
  const refKey = ref ? JSON.stringify(ref) : ''

  const request = useCallback(() => {
    if (!channel || !refKey) return
    const id = ++seq.current
    setState({ status: 'loading' })
    api.createHubContextLink(channel, JSON.parse(refKey) as HubContextRef).then(
      (link) => {
        if (id === seq.current) setState({ status: 'ready', link })
      },
      (err: unknown) => {
        if (id !== seq.current) return
        const error = err instanceof SliceApiError ? err : new SliceApiError('server error', 'server')
        setState({ status: 'error', error })
      },
    )
  }, [api, channel, refKey])

  useEffect(() => {
    request()
    return () => {
      // Ответ после ухода с экрана не нужен
      seq.current += 1
    }
  }, [request])

  const isFresh = useCallback(
    (link: HubContextLink) => now().getTime() < Date.parse(link.expiresAt) - HUB_LINK_MARGIN_MS,
    [now],
  )

  return { state, retry: request, isFresh }
}

// ---------- соревнования, сборы, аттестации (events.md) ----------

export function useEvents() {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'events'], queryFn: () => api.listEvents(), staleTime: 60_000 })
}

export function useEvent(id: string) {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'event', id], queryFn: () => api.getEvent(id) })
}

export function useEventParticipants(id: string) {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'event-participants', id], queryFn: () => api.listEventParticipants(id) })
}

export function useAthleteEvents(athleteId: string) {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'athlete-events', athleteId], queryFn: () => api.listAthleteEvents(athleteId) })
}

/** После изменения участников: список события, счётчики в списке, события спортсменов. */
export function useInvalidateEvent() {
  const client = useQueryClient()
  return useCallback(
    (eventId?: string) =>
      Promise.all([
        client.invalidateQueries({ queryKey: [ROOT, 'events'] }),
        client.invalidateQueries({ queryKey: [ROOT, 'athlete-events'] }),
        ...(eventId
          ? [
              client.invalidateQueries({ queryKey: [ROOT, 'event', eventId] }),
              client.invalidateQueries({ queryKey: [ROOT, 'event-participants', eventId] }),
            ]
          : []),
      ]),
    [client],
  )
}

/**
 * Одна запись за раз: пока запрос в полёте, второе нажатие ничего не шлёт
 * (null). Для создания, правки, удаления события и добавления участников.
 */
export function useSingleFlight() {
  const { platform } = useEnv()
  const inFlight = useRef(false)
  const [busy, setBusy] = useState(false)
  const run = useCallback(
    async <T>(call: () => Promise<T>): Promise<T | null> => {
      if (inFlight.current) return null
      inFlight.current = true
      setBusy(true)
      platform.haptic('select')
      try {
        const res = await call()
        platform.haptic('success')
        return res
      } catch (err) {
        platform.haptic('error')
        throw err
      } finally {
        inFlight.current = false
        setBusy(false)
      }
    },
    [platform],
  )
  return { run, busy }
}

/** Сохранение строки участника: как отметка посещаемости. */
export type ParticipantRowState =
  | { phase: 'saving'; patch: ParticipantPatch }
  | { phase: 'error'; patch: ParticipantPatch }

/**
 * Статус участия и взнос по касанию (events.md, как посещаемость по решению
 * владельца 7а): строка показывает «Сохраняю», при сбое - «Не сохранено» и
 * повтор. Список в кэше меняется только после ответа сервера. Повтор
 * безопасен: сервер записывает то же значение.
 */
export function useParticipantMarks(eventId: string) {
  const { api, platform } = useEnv()
  const client = useQueryClient()
  const [states, setStates] = useState<Record<string, ParticipantRowState>>({})

  const set = useCallback(
    async (athleteId: string, patch: ParticipantPatch) => {
      setStates((s) => ({ ...s, [athleteId]: { phase: 'saving', patch } }))
      platform.haptic('select')
      try {
        await api.updateEventParticipant(eventId, athleteId, patch)
        // Сервер принял значение: оно и показывается - в событии и в карточке спортсмена.
        const apply = <T extends Pick<EventParticipant, 'status' | 'payment' | 'result'>>(p: T): T => ({
          ...p,
          ...(patch.status ? { status: patch.status } : {}),
          ...(patch.payment ? { payment: patch.payment } : {}),
          ...(patch.result !== undefined ? { result: patch.result.trim() || undefined } : {}),
        })
        await client.cancelQueries({ queryKey: [ROOT, 'event-participants', eventId] })
        await client.cancelQueries({ queryKey: [ROOT, 'athlete-events', athleteId] })
        client.setQueryData<EventParticipant[]>([ROOT, 'event-participants', eventId], (list) =>
          list?.map((p) => (p.athleteId === athleteId ? apply(p) : p)),
        )
        client.setQueryData<AthleteEventItem[]>([ROOT, 'athlete-events', athleteId], (list) =>
          list?.map((i) => (i.event.id === eventId ? apply(i) : i)),
        )
        setStates((s) => {
          const next = { ...s }
          delete next[athleteId]
          return next
        })
      } catch {
        setStates((s) => ({ ...s, [athleteId]: { phase: 'error', patch } }))
        platform.haptic('error')
      }
    },
    [api, client, eventId, platform],
  )

  const retryFailed = useCallback(() => {
    for (const [athleteId, st] of Object.entries(states)) {
      if (st.phase === 'error') void set(athleteId, st.patch)
    }
  }, [set, states])

  const failed = Object.values(states).filter((s) => s.phase === 'error').length
  const saving = Object.values(states).filter((s) => s.phase === 'saving').length
  return { set, retryFailed, states, failed, saving }
}

// ---------- группы целиком (этап 5 шаг 1) ----------

/** Справочник тренеров текущего направления. */
export function useCoaches(enabled = true) {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'coaches'], queryFn: () => api.listCoaches(), staleTime: 60_000, enabled })
}

/**
 * После создания, правки или удаления группы: список групп, карточка группы,
 * занятия (Сегодня, расписание), доски занятий, спортсмены (подпись группы и
 * фильтр «Без группы»), справочник тренеров.
 */
export function useInvalidateGroups() {
  const client = useQueryClient()
  return useCallback(
    async (groupId?: string) => {
      await Promise.all([
        client.invalidateQueries({ queryKey: [ROOT, 'groups'] }),
        client.invalidateQueries({ queryKey: [ROOT, 'sessions'] }),
        client.invalidateQueries({ queryKey: [ROOT, 'board'] }),
        client.invalidateQueries({ queryKey: [ROOT, 'athletes'] }),
        client.invalidateQueries({ queryKey: [ROOT, 'coaches'] }),
        client.invalidateQueries({ queryKey: [ROOT, 'subscription'] }),
        ...(groupId ? [client.invalidateQueries({ queryKey: [ROOT, 'group', groupId] })] : []),
      ])
    },
    [client],
  )
}

// ---------- направления, справочник тренеров, мелочи клуба (этап 5 шаг 4) ----------

/** Содержимое направления для предпросмотра удаления и смены вида спорта. */
export function useDirectionContents(id: string, enabled = true) {
  const { api } = useEnv()
  return useQuery({
    queryKey: [ROOT, 'direction-contents', id],
    queryFn: () => api.getDirectionContents(id),
    enabled,
    staleTime: 0,
  })
}

/** Справочник тренеров текущего направления с контактами и группами. */
export function useCoachCards() {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'coach-cards'], queryFn: () => api.listCoachCards() })
}

export function useCoach(id: string) {
  const { api } = useEnv()
  return useQuery({ queryKey: [ROOT, 'coach', id], queryFn: () => api.getCoach(id) })
}

/**
 * После правки справочника тренеров: карточки и список тренеров, выбор
 * тренеров в форме группы, шапки групп (там тренеры по имени), лимиты.
 */
export function useInvalidateCoaches() {
  const client = useQueryClient()
  return useCallback(
    async (coachId?: string) => {
      await Promise.all([
        client.invalidateQueries({ queryKey: [ROOT, 'coach-cards'] }),
        client.invalidateQueries({ queryKey: [ROOT, 'coaches'] }),
        client.invalidateQueries({ queryKey: [ROOT, 'group'] }),
        client.invalidateQueries({ queryKey: [ROOT, 'subscription'] }),
        ...(coachId ? [client.invalidateQueries({ queryKey: [ROOT, 'coach', coachId] })] : []),
      ])
    },
    [client],
  )
}

// ---------- обратная связь как переписка ----------

/** Сколько ждать между проверками новых ответов поддержки на открытом экране. */
const FEEDBACK_POLL_MS = 30_000

/** Мои обращения. По unread - бейдж «Новый ответ» в «Клубе» и у обращения. */
export function useFeedbackThreads() {
  const { api } = useEnv()
  return useQuery({
    queryKey: [ROOT, 'feedback', 'threads'],
    queryFn: () => api.listFeedbackThreads(),
    refetchInterval: FEEDBACK_POLL_MS,
  })
}

/** Переписка целиком; пока экран открыт, ответ поддержки подтягивается сам. */
export function useFeedbackThread(id: string) {
  const { api } = useEnv()
  return useQuery({
    queryKey: [ROOT, 'feedback', 'thread', id],
    queryFn: () => api.getFeedbackThread(id),
    refetchInterval: FEEDBACK_POLL_MS,
  })
}

/** Перечитать список и тред после отправки или отметки «прочитано». */
export function useInvalidateFeedback() {
  const client = useQueryClient()
  return useCallback(
    async (threadId?: string) => {
      await Promise.all([
        client.invalidateQueries({ queryKey: [ROOT, 'feedback', 'threads'] }),
        ...(threadId ? [client.invalidateQueries({ queryKey: [ROOT, 'feedback', 'thread', threadId] })] : []),
      ])
    },
    [client],
  )
}
